import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'

import { errorReference, errorReporter } from './errorReporter'
import { createResourceCache, type Snapshot } from './resourceCache'

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly requestId?: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** The session ended (idle timeout or logout); nothing more can be sent until the user logs in. */
export const isSessionEnd = (error: unknown): error is ApiError & { status: 401 } =>
  error instanceof ApiError && error.status === 401

/** Listeners to one kind of answer; `on` returns the listener's removal. */
function signal() {
  const listeners = new Set<() => void>()
  return {
    on(listener: () => void): () => void {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    fire: () => listeners.forEach((listener) => listener()),
  }
}

const sessionEnd = signal()
const termsPending = signal()

/** Calls `listener` at every answer that the session has ended (401); returns its removal. */
export const onSessionEnd = sessionEnd.on

/** Calls `listener` at every refusal because the terms of use in force are not accepted yet (AUTH-10). */
export const onTermsPending = termsPending.on

export const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const RATE_LIMIT_RETRIES = 3
const MAX_RETRY_AFTER_MS = 30_000

/** The wait a 429 asks for (API-04), one second if it names none, never longer than 30 seconds. */
function retryAfter(response: Response): number {
  return Math.min(Number(response.headers.get('Retry-After')) * 1000 || 1000, MAX_RETRY_AFTER_MS)
}

type Detail = string | { msg: string; loc?: (string | number)[] }[] | undefined

function describe(detail: Detail, fallback: string): string {
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((item) => `${item.loc?.at(-1) ?? ''}: ${item.msg}`).join('; ')
  return fallback
}

/** Errors come back in the page's language (GEN-06), which the i18n provider sets on <html>. */
const language = () => globalThis.document?.documentElement.lang || 'en'

/** Sends a request to the API, again after a rate-limit refusal, and throws its error as an ApiError. */
async function call(path: string, init: RequestInit): Promise<Response> {
  const send = async () => {
    try { return await fetch(`/api${path}`, { ...init, credentials: 'same-origin' }) }
    catch (error) { errorReporter.report(error, { operation: 'network', path }); throw error }
  }
  let response = await send()
  // The rate limit refuses a request before anything happens, so any request may be sent again.
  for (let retry = 0; response.status === 429 && retry < RATE_LIMIT_RETRIES; retry++) {
    await sleep(retryAfter(response))
    response = await send()
  }
  if (!response.ok) {
    if (response.status === 401) sessionEnd.fire()
    const body = (await response.json().catch(() => ({}))) as { detail?: Detail }
    if (response.status === 403 && body.detail === 'terms_not_accepted') termsPending.fire()
    const error = new ApiError(response.status, describe(body.detail, response.statusText), response.headers.get('X-Request-Id') ?? undefined)
    if (response.status >= 500) errorReporter.report(error, { operation: 'http', path, status: response.status, requestId: error.requestId })
    throw error
  }
  return response
}

/**
 * Calls the API with a JSON body, or a file (`body`) sent as it is, with its own type. A request the
 * browser makes on its own (`background`), such as a poll, does not count as the user's activity,
 * so the session's idle timeout keeps running (AUTH-08).
 */
export async function api<T = void>(
  path: string,
  options: { method?: string; json?: unknown; body?: Blob; background?: boolean } = {},
): Promise<T> {
  const json = options.json !== undefined
  const body = json ? JSON.stringify(options.json) : options.body
  const headers: Record<string, string> = { Accept: 'application/json', 'Accept-Language': language() }
  if (body !== undefined) headers['Content-Type'] = json ? 'application/json' : options.body!.type
  if (options.background) headers['X-Pedfx-Background'] = '1'
  const response = await call(path, { method: options.method ?? (body !== undefined ? 'POST' : 'GET'), headers, body })
  return (response.status === 204 ? undefined : await response.json()) as T
}

/** A binary answer of the API, such as a rendered view (PRT-01). */
export async function apiBlob(path: string, signal?: AbortSignal): Promise<Blob> {
  return (await call(path, { headers: { 'Accept-Language': language() }, signal })).blob()
}

const KEEP_ALIVE_MS = 5 * 60_000

/**
 * Runs work that sends no request for a long time, such as checking thousands of files in the
 * browser, without the session's idle timeout (AUTH-08) ending it meanwhile.
 */
export async function keepingSession<T>(work: () => Promise<T>): Promise<T> {
  const timer = setInterval(() => void api('/me').catch(() => undefined), KEEP_ALIVE_MS)
  try {
    return await work()
  } finally {
    clearInterval(timer)
  }
}

/** The login, back to `next`; with `prompt` 'login' Keycloak asks for the password even while its own session lasts. */
export function loginUrl(next = window.location.pathname + window.location.search, prompt?: 'login'): string {
  return `/api/auth/login?next=${encodeURIComponent(next)}${prompt ? `&prompt=${prompt}` : ''}`
}

export interface Resource<T> {
  data?: T
  error?: ApiError
  /** The path `data` or `error` answers, which lags behind the path asked for while that loads. */
  answered?: string
  loading: boolean
  reload: () => void
  /** Loads again on the browser's own initiative, as a poll does: not the user's activity (AUTH-08). */
  poll: () => void
}

const resources = createResourceCache(
  (path, json, background) => api(path, { ...(json === undefined ? {} : { json }), background }),
  (error) => asApiError(error),
)
const noSubscription = () => () => undefined
const answers = (snapshot: Snapshot) => snapshot.data !== undefined || snapshot.error !== undefined

/** After a change: every shown resource under the path loads again, wherever it shows, such as the
 * notifications in the page and in the navigation's count (`resourceCache.invalidate`). */
export const invalidate = (prefix: string): void => resources.invalidate(prefix)

/**
 * Loads a resource by GET, or by POST with `json` (a search), once for every component that shows it
 * (`resourceCache`); keeps showing the previous data while reloading, and the previous path's answer
 * while another path loads, and loads again when the path or the body's content changes. `null` skips
 * loading.
 */
export function useApi<T>(path: string | null, json?: unknown): Resource<T> {
  const body = json === undefined ? undefined : JSON.stringify(json)
  const key = path === null ? null : body === undefined ? path : `${path}\u0000${body}`
  const subscribe = useCallback(
    (listener: () => void) =>
      key === null || path === null ? noSubscription() : resources.subscribe(key, path, body === undefined ? undefined : (JSON.parse(body) as unknown), listener),
    [key, path, body],
  )
  const snapshot = useSyncExternalStore(subscribe, () => (key === null ? NOT_ASKED : resources.snapshot(key)))
  // The answer shown last, which stays while a path without an answer of its own loads.
  const previous = useRef<Snapshot>(NOT_ASKED)
  useEffect(() => {
    if (answers(snapshot)) previous.current = snapshot
  }, [snapshot])
  const shown = answers(snapshot) ? snapshot : { ...previous.current, loading: snapshot.loading }
  const reload = useCallback(() => key !== null && resources.reload(key), [key])
  const poll = useCallback(() => key !== null && resources.poll(key), [key])
  return { ...(shown as Omit<Resource<T>, 'reload' | 'poll'>), reload, poll }
}

const NOT_ASKED: Snapshot = { loading: false }

const POLL_MS = 2000

/**
 * Polls a resource every two seconds (or `ms`) while `active`, for example while a worker job runs;
 * `poll` is the resource's own, so the polls leave the session's idle timeout running (AUTH-08).
 */
export function usePoll(active: boolean, poll: () => void, ms = POLL_MS): void {
  useEffect(() => {
    if (!active) return
    const timer = window.setInterval(poll, ms)
    return () => window.clearInterval(timer)
  }, [active, poll, ms])
}

export function asApiError(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, error instanceof Error ? error.message : String(error), errorReference(error))
}

/** Runs a user action and tracks its progress and error. */
export function useAction() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<ApiError>()
  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true)
    setError(undefined)
    try {
      await action()
    } catch (caught) {
      errorReporter.report(caught, { operation: 'action' })
      setError(asApiError(caught))
    } finally {
      setBusy(false)
    }
  }, [])
  return { busy, error, run }
}
